import {
  PerspectiveCamera,
  Vector3,
  Raycaster,
  Mesh,
  Object3D,
  Vector2,
  Box3,
  Material,
  MeshBasicMaterial,
  DoubleSide,
  FrontSide,
  ArrowHelper,
  PlaneBufferGeometry,
} from "three";
import { PointerLockControls } from "./lib/PointerLockControls";
import {
  ActionType,
  FocusedObjectIcon,
  FocusedObjectType,
  ModalType,
  World,
} from "../contexts/AppContext";
import ScreenSpacePosition from "./ScreenSpacePosition";
import { ControlsMode } from "../contexts/AppContext";
import CameraShake from "./CameraShake";
import content from "../content.json";
import { disposeComplete } from "../util/three-utils";

const urlParams = new URLSearchParams(window.location.search);
const SHOW_RAY = urlParams.get("show-ray") === "true";

const SIZE_MULTIPLIER = 1;
const MOVEMENT_MULTIPLIER = 0.0075;

const VIEW_HEIGHT = 1.6 * SIZE_MULTIPLIER;
const MIN_DIST_TO_WALL = 0.15 * SIZE_MULTIPLIER;
const MAX_DIST_TO_FOCUS_HIT = 4.5 * SIZE_MULTIPLIER;

const DOWN = new Vector3(0, -1, 0);
const TMP_VEC3 = new Vector3();
const TMP_VEC3_2 = new Vector3();

const HIDDEN_MATERIAL_WALL = new MeshBasicMaterial({
  transparent: true,
  opacity: SHOW_RAY ? 0.25 : 0,
  wireframe: false,
  color: 0xff0000,
  side: DoubleSide,
  depthTest: false,
  depthWrite: false,
});

const HIDDEN_MATERIAL_TRIGGER = new MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  wireframe: false,
  color: 0x00ff00,
  side: DoubleSide,
  depthTest: false,
  depthWrite: false,
});

const HIDDEN_MATERIAL_PATH = new MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  wireframe: false,
  color: 0x0000ff,
  side: DoubleSide,
  depthTest: false,
  depthWrite: false,
});

const RAY_HELPER = new ArrowHelper(
  new Vector3(1, 0, 0),
  new Vector3(),
  1,
  0xff0000
);

RAY_HELPER.line.add(
  new Mesh(
    new PlaneBufferGeometry(0.5, 0.5).rotateX(Math.PI * 0.5),
    new MeshBasicMaterial({ color: 0xff00ff, side: DoubleSide })
  )
);

RAY_HELPER.line.children[0].position.y = MIN_DIST_TO_WALL;

export default class CameraControls {
  private baseControls: PointerLockControls;
  private cameraShake: CameraShake;
  private mode = (window as any).state.controlsMode;
  private colissionMeshes: Mesh;
  private floor: Mesh;
  private triggers: Mesh[] = [];
  private looks: Mesh[] = [];
  private plants: Mesh[] = [];
  private worldPortals: Mesh[] = [];
  private activeTrigger: Object3D | null = null;
  private focusedObjectScreenSpacePosition: ScreenSpacePosition | null = null;
  private focusVec = new Vector2();
  private loops = 0;
  private previousPosition = new Vector3();
  private previousMovementDirection = new Vector3();
  private camera: PerspectiveCamera;
  private cameraPlayer = new Object3D();
  private mouseWheelDelta = 0;

  private raycaster = new Raycaster(
    new Vector3(),
    new Vector3().copy(DOWN),
    0,
    VIEW_HEIGHT
  );

  private moveForward = false;
  private moveBackward = false;
  private moveLeft = false;
  private moveRight = false;

  public velocity = new Vector3();
  private direction = new Vector3();

  constructor(
    camera: PerspectiveCamera,
    domElement: HTMLElement,
    startPosition: Object3D
  ) {
    this.camera = camera;
    this.cameraShake = new CameraShake(this.camera);
    this.colissionMeshes = camera.parent.getObjectByName("Collision") as Mesh;
    this.colissionMeshes.traverse((path) => {
      ((path as Mesh).material as Material)?.dispose();
      (path as Mesh).material = HIDDEN_MATERIAL_WALL;
    });
    this.floor = camera.parent.getObjectByName("Collision_Floor") as Mesh;
    camera.parent.add(this.cameraPlayer);
    camera.parent.traverse((child) => {
      if (
        child.name.indexOf("Placeholder") === 0 ||
        child.name.indexOf("Path") === 0
      ) {
        this.triggers.push(child as Mesh);

        if (child.name.indexOf("Placeholder_Manifesto") === 0) {
          (child as Mesh).material = HIDDEN_MATERIAL_TRIGGER;
        } else if (child.name.indexOf("Placeholder_Plant") === 0) {
          this.plants.push(child as Mesh);
        } else if (child.name.indexOf("Placeholder_Look") === 0) {
          this.looks.push(child as Mesh);
        } else if (child.name.indexOf("Path") === 0) {
          this.worldPortals.push(child as Mesh);
          ((child as Mesh).material as Material)?.dispose();
          (child as Mesh).material = HIDDEN_MATERIAL_PATH;
        }
      }
    });
    this.baseControls = new PointerLockControls(this.cameraPlayer, domElement);

    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onMouseWheel = this.onMouseWheel.bind(this);

    if (this.mode === ControlsMode.Pointerlock) {
      document.addEventListener("keydown", this.onKeyDown);
      document.addEventListener("keyup", this.onKeyUp);
      document.addEventListener("wheel", this.onMouseWheel);
    }

    if (this.mode === ControlsMode.Pointerlock) {
      this.baseControls.minPolarAngle = Math.PI * 0.16;
      this.baseControls.maxPolarAngle = Math.PI * 0.84;
    } else {
      this.baseControls.minPolarAngle = Math.PI * 0.33;
      this.baseControls.maxPolarAngle = Math.PI * 0.66;
    }

    this.previousPosition.copy(startPosition.position);
    this.cameraPlayer.position.copy(startPosition.position);
    this.cameraPlayer.rotation.copy(startPosition.rotation);

    if (SHOW_RAY) {
      this.cameraPlayer.parent.add(RAY_HELPER);
    }
  }

  public get speed(): number {
    return this.velocity.length();
  }

  private onMouseWheel(e) {
    const MAX_DELTA = 50;
    let delta = e.deltaY;
    if (delta > MAX_DELTA) {
      delta = MAX_DELTA;
    } else if (delta < -MAX_DELTA) {
      delta = -MAX_DELTA;
    }
    this.mouseWheelDelta += delta;

    (window as any).dispatch({
      type: ActionType.HideControlsHint,
    });
  }

  private onKeyDown(event: KeyboardEvent) {
    if (!this.baseControls.isLocked) return;

    switch (event.code) {
      case "ArrowUp":
      case "KeyW":
        (window as any).dispatch({
          type: ActionType.HideControlsHint,
        });
        this.moveForward = true;
        break;

      case "ArrowLeft":
      case "KeyA":
        (window as any).dispatch({
          type: ActionType.HideControlsHint,
        });
        this.moveLeft = true;
        break;

      case "ArrowDown":
      case "KeyS":
        (window as any).dispatch({
          type: ActionType.HideControlsHint,
        });
        this.moveBackward = true;
        break;

      case "ArrowRight":
      case "KeyD":
        (window as any).dispatch({
          type: ActionType.HideControlsHint,
        });
        this.moveRight = true;
        break;
    }
  }

  private onKeyUp(event: KeyboardEvent) {
    if (!this.baseControls.isLocked) return;

    switch (event.code) {
      case "ArrowUp":
      case "KeyW":
        this.moveForward = false;
        break;

      case "ArrowLeft":
      case "KeyA":
        this.moveLeft = false;
        break;

      case "ArrowDown":
      case "KeyS":
        this.moveBackward = false;
        break;

      case "ArrowRight":
      case "KeyD":
        this.moveRight = false;
        break;
    }
  }

  private getFloorIntersections() {
    this.raycaster.far = VIEW_HEIGHT + 0.05;
    this.raycaster.ray.direction.copy(DOWN);
    this.raycaster.ray.origin.copy(this.baseControls.getObject().position);

    const intersections = this.raycaster.intersectObject(this.floor, false);

    return intersections;
  }

  // _vector.setFromMatrixColumn(camera.matrix, 0);
  // _vector.crossVectors(camera.up, _vector);
  // camera.position.addScaledVector(_vector, distance);

  // this.baseControls.moveRight(-this.velocity.x * delta);
  // this.baseControls.moveForward(-this.velocity.z * delta);

  // private getWallIntersections() {
  //   var futureVelocity = new Vector3();
  //   futureVelocity.x += this.direction.x;
  //   futureVelocity.z += this.direction.z;

  //   var futurePosition = new Vector3();

  //   // move right
  //   TMP_VEC3.setFromMatrixColumn(this.baseControls.getObject().matrix, 0);
  //   TMP_VEC3.crossVectors(this.baseControls.getObject().up, TMP_VEC3);

  //   futurePosition.addScaledVector(TMP_VEC3, -futureVelocity.x);

  //   // move forward
  //   TMP_VEC3.setFromMatrixColumn(this.baseControls.getObject().matrix, 0);
  //   futurePosition.addScaledVector(TMP_VEC3, -futureVelocity.z);

  //   // // the next lines transform the direction to be relative to the camera to use as the ray direction.
  //   // TMP_VEC3_2.set(0, 0, 0);

  //   // TMP_VEC3.setFromMatrixColumn(this.baseControls.getObject().matrix, 0);
  //   // TMP_VEC3.crossVectors(this.baseControls.getObject().up, TMP_VEC3);

  //   // TMP_VEC3_2.addScaledVector(TMP_VEC3, this.velocity.z);
  //   // TMP_VEC3_2.addScaledVector(TMP_VEC3, this.direction.z);
  //   // TMP_VEC3_2.addScaledVector(TMP_VEC3, this.velocity.x);
  //   // TMP_VEC3_2.addScaledVector(TMP_VEC3, this.direction.x);

  //   this.raycaster.far = MIN_DIST_TO_WALL;
  //   this.raycaster.ray.direction.copy(TMP_VEC3.normalize());
  //   this.raycaster.ray.origin.copy(this.baseControls.getObject().position);
  //   this.raycaster.ray.origin.y -= VIEW_HEIGHT * 0.5;

  //   RAY_HELPER.position.copy(this.raycaster.ray.origin);
  //   RAY_HELPER.position.y += 0.5;
  //   RAY_HELPER.setDirection(this.raycaster.ray.direction);

  //   const intersections = this.raycaster.intersectObject(
  //     this.colissionMeshes,
  //     true
  //   );

  //   return intersections;
  // }

  private getWallIntersections(delta) {
    // the next lines transform the direction to be relative to the camera to use as the ray direction.
    TMP_VEC3_2.set(0, 0, 0);

    // move foward
    TMP_VEC3.setFromMatrixColumn(this.baseControls.getObject().matrix, 0);
    TMP_VEC3.crossVectors(this.baseControls.getObject().up, TMP_VEC3);
    TMP_VEC3_2.addScaledVector(TMP_VEC3, this.velocity.z); // velocity

    // move right
    TMP_VEC3.setFromMatrixColumn(this.baseControls.getObject().matrix, 0);
    TMP_VEC3_2.addScaledVector(TMP_VEC3, this.velocity.x); // velocity

    // direction from this position to next position (TMP_VEC3_2)
    TMP_VEC3.copy(this.cameraPlayer.position)
      .add(TMP_VEC3_2)
      .sub(this.previousPosition)
      .normalize();

    const velocity = TMP_VEC3_2.length() * delta;
    const rayFar = Math.max(MIN_DIST_TO_WALL, velocity * 1.2);

    this.raycaster.far = rayFar;
    this.raycaster.ray.origin.copy(this.baseControls.getObject().position);
    this.raycaster.ray.origin.y -= VIEW_HEIGHT * 0.5;

    if (TMP_VEC3.distanceTo(this.previousPosition) > 0) {
      this.previousMovementDirection.copy(TMP_VEC3);
      this.raycaster.ray.direction.copy(TMP_VEC3);
    } else {
      this.raycaster.ray.direction.copy(this.previousMovementDirection);
    }

    if (SHOW_RAY) {
      RAY_HELPER.line.children[0].position.y = rayFar;
      RAY_HELPER.position.copy(this.raycaster.ray.origin);
      RAY_HELPER.position.y += 0.5;
      RAY_HELPER.setDirection(this.raycaster.ray.direction);
    }

    const intersections = this.raycaster.intersectObject(
      this.colissionMeshes,
      true
    );

    return intersections;
  }

  private onEnterTrigger(name: string) {
    if (name.indexOf("Placeholder_Manifesto") === 0) {
      this.disable();
      (window as any).dispatch({
        type: ActionType.SetModal,
        value: {
          type: ModalType.Manifesto,
        },
      });
    } else if (name.indexOf("Path") === 0) {
      this.disable();
      (window as any).dispatch({
        type: ActionType.SetWorld,
        value: World[name.replace("Path_", "")],
      });
    }
  }

  private checkTriggerIntersections() {
    this.raycaster.far = VIEW_HEIGHT + 1.5;
    this.raycaster.ray.direction.copy(DOWN);
    this.raycaster.ray.origin.copy(this.baseControls.getObject().position);

    const intersections = this.raycaster.intersectObjects(this.triggers, true);

    const hitsTrigger = intersections.length > 0;
    if (hitsTrigger && intersections[0].object !== this.activeTrigger) {
      this.activeTrigger = intersections[0].object;
      this.onEnterTrigger(this.activeTrigger.name);
    } else if (!hitsTrigger && !!this.activeTrigger) {
      this.activeTrigger = null;
    }

    return hitsTrigger;
  }

  private checkFocusIntersections() {
    this.raycaster.far = MAX_DIST_TO_FOCUS_HIT;
    this.raycaster.setFromCamera(this.focusVec, this.camera);

    const plantIntersections = this.raycaster.intersectObjects(
      this.plants,
      false
    );
    const hitsPlant = plantIntersections.length > 0;
    if (hitsPlant) {
      if (!this.focusedObjectScreenSpacePosition) {
        const boundingBox = new Box3().setFromObject(
          plantIntersections[0].object
        );
        const size = boundingBox.getSize(TMP_VEC3);

        this.focusedObjectScreenSpacePosition = new ScreenSpacePosition(
          plantIntersections[0].object,
          this.camera as PerspectiveCamera,
          new Vector3(0, size.y * 0.25, 0)
        );
        (window as any).dispatch({
          type: ActionType.SetFocusedObject,
          value: {
            object: plantIntersections[0].object,
            screenSpacePosition: this.focusedObjectScreenSpacePosition,
            name: plantIntersections[0].object.name,
            icon: FocusedObjectIcon.Eye,
            type: FocusedObjectType.Plant,
          },
        });
      }
    }

    const lookIntersections = this.raycaster.intersectObjects(
      this.looks,
      false
    );
    const hitsLook = lookIntersections.length > 0;
    if (hitsLook) {
      if (!this.focusedObjectScreenSpacePosition) {
        const boundingBox = new Box3().setFromObject(
          lookIntersections[0].object
        );
        const size = boundingBox.getSize(TMP_VEC3);

        this.focusedObjectScreenSpacePosition = new ScreenSpacePosition(
          lookIntersections[0].object,
          this.camera as PerspectiveCamera,
          new Vector3(-0.8, size.y * 0.133, 0)
        );
        (window as any).dispatch({
          type: ActionType.SetFocusedObject,
          value: {
            object: lookIntersections[0].object,
            screenSpacePosition: this.focusedObjectScreenSpacePosition,
            name: lookIntersections[0].object.name,
            icon: FocusedObjectIcon.Eye,
            type: FocusedObjectType.Look,
          },
        });
      }
    }

    const worldPortalIntersections = this.raycaster.intersectObjects(
      this.worldPortals,
      false
    );
    const hitsWorldPortal = worldPortalIntersections.length > 0;
    if (hitsWorldPortal) {
      if (!this.focusedObjectScreenSpacePosition) {
        this.focusedObjectScreenSpacePosition = new ScreenSpacePosition(
          worldPortalIntersections[0].object,
          this.camera as PerspectiveCamera,
          new Vector3(0, 0.5, 0)
        );
        (window as any).dispatch({
          type: ActionType.SetFocusedObject,
          value: {
            object: worldPortalIntersections[0].object,
            screenSpacePosition: this.focusedObjectScreenSpacePosition,
            name: content.worlds[
              worldPortalIntersections[0].object.name.replace("Path_", "")
            ].name,
            icon: FocusedObjectIcon[
              worldPortalIntersections[0].object.name.replace("Path_", "")
            ],
            type: FocusedObjectType.World,
          },
        });
      }
    }

    if (
      !hitsLook &&
      !hitsWorldPortal &&
      !hitsPlant &&
      !!this.focusedObjectScreenSpacePosition
    ) {
      this.focusedObjectScreenSpacePosition = null;
      (window as any).dispatch({
        type: ActionType.SetFocusedObject,
        value: null,
      });
    }
  }

  public update(delta: number): void {
    if (this.mode === ControlsMode.Joystick) {
      if (
        (window as any).state.showControlsMoveHint &&
        (Math.abs((window as any).joystick.right.x) > 0.2 ||
          Math.abs((window as any).joystick.right.y) > 0.2 ||
          Math.abs((window as any).joystick.left.x) > 0.2 ||
          Math.abs((window as any).joystick.left.y) > 0.2)
      ) {
        (window as any).dispatch({
          type: ActionType.HideControlsHint,
        });
      }
      const { x, y } = (window as any).joystick.right;
      const { x: xMove } = (window as any).joystick.left;
      this.baseControls.onJoystickLookMove({
        x: x * 1 + xMove * 0.9,
        y: y * -1,
      });
    }
    const intersections = this.getFloorIntersections();

    const onObject = intersections.length > 0;
    if (onObject) {
      var terrainHeight =
        this.baseControls.getObject().position.y - intersections[0].distance;
      this.baseControls.getObject().position.y = terrainHeight + VIEW_HEIGHT;
    }

    this.velocity.x -= this.velocity.x * 0.1 * SIZE_MULTIPLIER * delta;
    this.velocity.z -= this.velocity.z * 0.1 * SIZE_MULTIPLIER * delta;

    this.velocity.y -= 0.03 * delta;

    this.mouseWheelDelta *= 0.001 * delta;
    if (Math.abs(this.mouseWheelDelta) < 0.01) {
      this.mouseWheelDelta = 0;
    }

    if (this.mode === ControlsMode.Pointerlock) {
      this.direction.z =
        Number(this.moveForward) -
        Number(this.moveBackward) +
        this.mouseWheelDelta * 0.001;
      this.direction.x = (Number(this.moveRight) - Number(this.moveLeft)) * 0.1; // left right movement slower than forward back
      this.direction.normalize(); // this ensures consistent movements in all directions
    } else {
      this.direction.z = (window as any).joystick.left.y * 0.75;
      this.direction.x = (window as any).joystick.left.x * 0.18;
    }

    if (
      this.moveForward ||
      this.moveBackward ||
      !!this.mouseWheelDelta ||
      this.mode === ControlsMode.Joystick
    ) {
      this.velocity.z +=
        this.direction.z * MOVEMENT_MULTIPLIER * SIZE_MULTIPLIER * delta;
    }
    if (
      this.moveLeft ||
      this.moveRight ||
      this.mode === ControlsMode.Joystick
    ) {
      this.velocity.x +=
        this.direction.x * MOVEMENT_MULTIPLIER * SIZE_MULTIPLIER * delta;
    }

    const obstacleIntersections = this.getWallIntersections(delta);
    const hitsObstacle = obstacleIntersections.length > 0;

    this.previousPosition.copy(this.cameraPlayer.position);
    // weird bug but if this runs on the first update loop, it intersects all triggers. IDK why
    if (this.loops > 0 && this.loops % 10 === 0) {
      this.checkTriggerIntersections();
      this.checkFocusIntersections();
    }

    if (hitsObstacle) {
      // do not move, and cancel out the direction velocity
      // this.velocity.projectOnPlane(this.direction);
      this.velocity.x = 0;
      this.velocity.z = 0;
    } else {
      this.baseControls.moveRight(this.velocity.x * delta);
      this.baseControls.moveForward(this.velocity.z * delta);
    }

    if (onObject === true) {
      this.velocity.y = Math.max(0, this.velocity.y);
    }

    this.baseControls.getObject().position.y += this.velocity.y * delta;

    if (this.baseControls.getObject().position.y < VIEW_HEIGHT) {
      this.velocity.y = 0;
      this.baseControls.getObject().position.y = VIEW_HEIGHT;
    }

    if (this.speed < 0.0001) {
      this.velocity.x = 0;
      this.velocity.y = 0;
      this.velocity.z = 0;
    }

    this.camera.position.copy(this.cameraPlayer.position);
    this.camera.rotation.copy(this.cameraPlayer.rotation);

    this.cameraShake.update(delta, this.velocity.length());

    this.loops++;
  }

  public enable(): void {
    if (this.mode === ControlsMode.Pointerlock) {
      this.baseControls?.lock();
    }
  }

  public disable(): void {
    this.moveForward = false;
    this.moveBackward = false;
    this.moveLeft = false;
    this.moveRight = false;
    if (this.mode === ControlsMode.Pointerlock) {
      this.baseControls?.unlock();
    }
  }

  public dispose(): void {
    if (this.mode === ControlsMode.Pointerlock) {
      this.baseControls?.unlock();
      document.removeEventListener("keydown", this.onKeyDown);
      document.removeEventListener("keyup", this.onKeyUp);
    }
    this.baseControls?.dispose();

    if (this.focusedObjectScreenSpacePosition) {
      (window as any).dispatch({
        type: ActionType.SetFocusedObject,
        value: null,
      });
    }

    this.moveForward = false;
    this.moveBackward = false;
    this.moveLeft = false;
    this.moveRight = false;
  }
}
